import { useEffect, useState } from "react";

import { useFetchData } from "../../apiHelpers";
import Button from "../../components/Button";
import CardWithTable from "../../components/CardWithTable";
import SearchProposals from "../../components/SearchProposals";
import Toolbar from "../../components/Toolbar";
import { API_ROUTES, PROPOSALS_COLUMNS } from "../../constants";
import { getOwnerType } from "../../helpers";
import useProposalListingStore from "../../store/ProposalListing.store";
import useUserStore from "../../store/User.store";
import { trackNewProposal } from "../../utils/helpers/analytics";
import { searchProposals } from "../../utils/helpers/intakeForm";
import {
  filterListByKeywordGivenKeyNames,
  getTextOccurrencesOnListByKeyName,
} from "../../utils/helpers/objects";

import "./proposals.scss";

const Proposals = () => {
  //#region //! States
  const [toggleSelectedOption, setToggleSelectedOption] = useState("left");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [filters, setFilters] = useState([]);
  const [dataToBeShown, setDataToBeShown] = useState(undefined);
  const [ownerCount, setOwnerCount] = useState({
    all: 0,
    my: 0,
  });
  //#endregion

  //#region //! Get store actions and data
  const { handleSetTableData } = useProposalListingStore(
    (state) => state.actions
  );
  const { email: userEmail } = useUserStore((state) => state.data.user);
  //#endregion

  //#region //! Fetching data
  const { data: listingData, isLoading } = useFetchData({
    url: API_ROUTES.PROPOSAL_LIST,
  });
  //#endregion

  //#region //! Form configs
  const searchProps = {
    name: "search",
    label: "Search proposals, advisors, strategies, and reports",
    setSearchKeyword,
    options: {
      setValueAs: (v) => (v.trim().length > 0 ? v : ""),
    },
  };
  //#endregion

  //#region //! Initializing stores
  useEffect(() => {
    if (isLoading) return;

    const allProposals = handleSetTableData(listingData);
    const myProposals = filterListByKeywordGivenKeyNames(
      allProposals,
      userEmail,
      ["requesterEmail"]
    );

    const searchedDateByOwner = {
      all: searchProposals(allProposals, searchKeyword),
      my: searchProposals(myProposals, searchKeyword),
    };
    const ownerType = getOwnerType(toggleSelectedOption);

    setDataToBeShown(searchedDateByOwner[ownerType]);
    setOwnerCount({
      all: searchedDateByOwner.all.length,
      my: searchedDateByOwner.my.length,
    });
    setFilters(
      getTextOccurrencesOnListByKeyName(
        searchedDateByOwner[ownerType],
        "sector"
      )
    );
  }, [
    handleSetTableData,
    isLoading,
    listingData,
    searchKeyword,
    toggleSelectedOption,
    userEmail,
  ]);
  //#endregion

  const headerCTA = (
    <Button
      href="/proposals/add"
      size="small"
      className="toolbarHeaderCta"
      iconName="system-add"
      iconPosition="right"
      onClick={trackNewProposal}
    >
      Create New Proposal
    </Button>
  );

  return (
    <>
      <header className="section-title">
        <div>
          <h1>Proposals</h1>
          {searchKeyword?.length > 0 && (
            <p className="search-result">
              {`${dataToBeShown?.length} search results for "`}
              <span>{searchKeyword}</span>
              {`"`}
            </p>
          )}
        </div>
        <SearchProposals {...searchProps} />
      </header>

      <CardWithTable
        isLoading={isLoading || !dataToBeShown}
        pluralTitle="Proposals"
        singularTitle="Proposal"
        columns={PROPOSALS_COLUMNS}
        searchKeyword={searchKeyword}
        onClickToggle={setToggleSelectedOption}
        ownerCount={{ ...ownerCount }}
        toggleSelectedOption={toggleSelectedOption}
        ownerType={getOwnerType(toggleSelectedOption)}
        filters={filters}
        tableData={dataToBeShown}
      />

      <div className="list-disclosures">
        <h3>Disclosures</h3>
        <p>For Investment Professional Use Only</p>
      </div>
      <Toolbar
        title="Quick Actions"
        data={null}
        headerCTA={headerCTA}
        collapsible={false}
      />
    </>
  );
};

export default Proposals;
